import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import { useTheme } from '../../contexts/ThemeContext'

const STANDARDS_DATA = [
  {
    standard: 'CE Marking',
    description: 'Mandatory for products sold in the EU/EEA',
    categories: ['Electronics', 'Toys', 'Machinery', 'Medical Devices', 'PPE'],
    requirements: [
      'Technical documentation',
      'Conformity assessment',
      'Declaration of Conformity',
      'CE mark affixed to product',
    ],
    timeline: '2-8 weeks',
  },
  {
    standard: 'UKCA Marking',
    description: 'Required for products sold in Great Britain',
    categories: ['Electronics', 'Toys', 'Machinery', 'Medical Devices', 'PPE'],
    requirements: [
      'UK Conformity Assessment',
      'UK Declaration of Conformity',
      'UKCA mark on product/packaging',
      'UK Responsible Person details',
    ],
    timeline: '4-12 weeks',
  },
  {
    standard: 'WEEE Compliance',
    description: 'Waste Electrical and Electronic Equipment regulations',
    categories: ['Electronics', 'Electrical Equipment'],
    requirements: [
      'Producer registration',
      'Collection/recycling obligations',
      'WEEE crossed-out bin symbol',
      'Take-back scheme participation',
    ],
    timeline: '2-4 weeks',
  },
  {
    standard: 'RoHS Compliance',
    description: 'Restriction of Hazardous Substances in electronics',
    categories: ['Electronics', 'Electrical Equipment'],
    requirements: [
      'Material declarations from suppliers',
      'Testing/certification',
      'Technical documentation',
      'Supply chain due diligence',
    ],
    timeline: '2-6 weeks',
  },
  {
    standard: 'REACH Compliance',
    description: 'Chemical substances regulation',
    categories: ['All products containing chemicals'],
    requirements: [
      'SVHC screening',
      'Safety Data Sheets',
      'SCIP database notification',
      'Article 33 communication',
    ],
    timeline: '4-8 weeks',
  },
  {
    standard: 'Packaging Compliance',
    description: 'Extended Producer Responsibility for packaging',
    categories: ['All packaged products'],
    requirements: [
      'Producer registration in each country',
      'Packaging weight reporting',
      'Recycling fee payments',
      'Recycling symbols on packaging',
    ],
    timeline: '2-4 weeks per country',
  },
]

export const ProductStandardsPage: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/compliance')}
            style={{
              background: 'none',
              border: 'none',
              color: colors.primary,
              cursor: 'pointer',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          >
            &larr; Back to Compliance Center
          </button>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
            Product Standards & Certifications
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
            CE marking, product safety, and labeling requirements for European markets
          </p>
        </div>

        {/* Key Alert */}
        <Card style={{ marginBottom: '32px', borderLeft: '4px solid #FF6B35', backgroundColor: '#FF6B3510' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <span style={{ fontSize: '32px' }}>&#9888;</span>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: colors.textPrimary }}>
                Important: Post-Brexit Changes
              </h3>
              <p style={{ color: colors.textSecondary, lineHeight: 1.6 }}>
                Products sold in the EU now require CE marking with an EU-based Responsible Person.
                Products sold in Great Britain require UKCA marking. CE marking is no longer accepted
                for most product categories in GB.
              </p>
            </div>
          </div>
        </Card>

        {/* Standards Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
          {STANDARDS_DATA.map((std) => (
            <Card key={std.standard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px', color: colors.textPrimary }}>
                    {std.standard}
                  </h3>
                  <p style={{ fontSize: '14px', color: colors.textSecondary }}>{std.description}</p>
                </div>
                <span style={{
                  padding: '4px 10px',
                  backgroundColor: colors.primary + '15',
                  borderRadius: '12px',
                  fontSize: '12px',
                  color: colors.primary,
                }}>
                  {std.timeline}
                </span>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: 600, color: colors.textSecondary, marginBottom: '8px' }}>
                  APPLICABLE CATEGORIES
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {std.categories.map((cat, idx) => (
                    <span key={idx} style={{
                      padding: '4px 10px',
                      backgroundColor: colors.background,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: colors.textPrimary,
                    }}>
                      {cat}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 600, color: colors.textSecondary, marginBottom: '8px' }}>
                  REQUIREMENTS
                </h4>
                <ul style={{ margin: 0, paddingLeft: '16px', color: colors.textSecondary, fontSize: '14px', lineHeight: 1.8 }}>
                  {std.requirements.map((req, idx) => (
                    <li key={idx}>{req}</li>
                  ))}
                </ul>
              </div>
            </Card>
          ))}
        </div>
      </main>
    </div>
  )
}
